import React, { useState } from "react";
import { NavLink as NavLinkRRD, Link, useNavigate } from "react-router-dom";
import {
  Collapse,
  NavbarBrand,
  Navbar,
  NavItem,
  NavLink,
  Nav,
  Container,
} from "reactstrap";
import getPrivilegeList from "../../PrivilegeList";
import { isFintechUser } from "../../util/common";

// Primary NBFC navigation shown in the admin sidebar.
// `privilege` names the flag in PrivilegeList that controls each link.
// `fintech: true` marks the links a fintech (LSP) login is allowed to see — such a user gets
// exactly those, regardless of the NBFC privilege flags.
const NAV_ITEMS = [
  { name: "Dashboard", path: "/admin/dashboard", icon: "ni ni-chart-pie-35 text-primary", fintech: true },
  { name: "Lead", path: "/admin/lead-list", icon: "ni ni-badge text-primary", privilege: "navLeadShow", fintech: true },
  { name: "Loan", path: "/admin/loan-list", icon: "ni ni-money-coins text-orange", privilege: "navLoanShow", fintech: true },
  { name: "Customer", path: "/admin/customer-list", icon: "ni ni-single-02 text-info", privilege: "navCustomerShow", fintech: true },
  { name: "Repayment", path: "/admin/repayment", icon: "ni ni-credit-card text-green", privilege: "navRepaymentShow" },
  { name: "Collection", path: "/admin/collection", icon: "ni ni-chart-bar-32 text-red", privilege: "navCollectionShow" },
  {
    name: "Reconciliation",
    icon: "ni ni-money-coins text-green",
    privilege: "navReconcillationShow",
    children: [
      { name: "Credit Line", path: "/admin/payment-reco" },
      { name: "Loan", path: "/admin/loan-reco" },
    ],
  },
  { name: "Reports", path: "/admin/merchant-reports", icon: "ni ni-shop text-info" },
  { name: "Loan Statement / Card", path: "/admin/loan-documents", icon: "ni ni-single-copy-04 text-info" },
  // Reporting and Training hidden from the sidebar for now.
  // {
  //   name: "Reporting",
  //   icon: "ni ni-chart-bar-32 text-primary",
  //   children: [
  //     { name: "Portfolio Report", path: "/admin/reporting/portfolio" },
  //   ],
  // },
  // { name: "Training", path: "/admin/training", icon: "ni ni-books text-primary" },
];

const AdminSidebar = ({ logo }) => {
  const [collapseOpen, setCollapseOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // name of the expanded submenu
  const navigate = useNavigate();

  // Read on every render so a fresh login is picked up without a reload.
  const privilegeList = getPrivilegeList();
  const fintechUser = isFintechUser();

  // Fintech (LSP) logins get the fintech-facing links only; NBFC staff go by privilege flags.
  const visibleItems = NAV_ITEMS.filter((item) =>
    fintechUser ? item.fintech : !item.privilege || privilegeList[item.privilege]
  );

  const toggleCollapse = () => setCollapseOpen((v) => !v);
  const closeCollapse = () => setCollapseOpen(false);
  const toggleMenu = (name) => setOpenMenu((cur) => (cur === name ? null : name));

  const handleLogout = () => {
    localStorage.removeItem("auth_data");
    sessionStorage.setItem("logout", "true");
    navigate("/auth/login");
    localStorage.clear();
  };

  return (
    <Navbar
      className="navbar-vertical fixed-left navbar-light bg-white"
      expand="md"
      id="sidenav-main"
    >
      <Container fluid className="d-flex flex-column" style={{ minHeight: "100%" }}>
        {/* Mobile toggle */}
        <button
          className="navbar-toggler"
          type="button"
          onClick={toggleCollapse}
        >
          <span className="navbar-toggler-icon" />
        </button>

        {/* Brand */}
        {logo && (
          <NavbarBrand className="pt-0" to={logo.innerLink} tag={Link}>
            <img
              alt={logo.imgAlt}
              className="navbar-brand-img"
              src={logo.imgSrc}
            />
          </NavbarBrand>
        )}

        <Collapse navbar isOpen={collapseOpen} className="d-flex flex-column flex-grow-1">
          <Nav navbar>
            {visibleItems.map((item) =>
              item.children ? (
                <React.Fragment key={item.name}>
                  <NavItem>
                    <NavLink
                      onClick={() => toggleMenu(item.name)}
                      className="d-flex align-items-center"
                      style={{ cursor: "pointer", flexWrap: "nowrap" }}
                    >
                      <i className={item.icon} />
                      <span className="flex-grow-1">{item.name}</span>
                      {/* Chevron sits flush right, vertically centred with the label.
                          Theme CSS gives sidebar icons a min-width/margin, which pushed it
                          onto its own line — reset both here. */}
                      <i
                        className="ni ni-bold-down"
                        style={{
                          fontSize: "0.7rem",
                          minWidth: 0,
                          flexShrink: 0,
                          marginLeft: "auto",
                          marginRight: 0,
                          transition: "transform 0.2s ease",
                          transform: openMenu === item.name ? "rotate(0deg)" : "rotate(-90deg)",
                        }}
                      />
                    </NavLink>
                  </NavItem>
                  <Collapse isOpen={openMenu === item.name}>
                    {item.children.map((c) => (
                      <NavItem key={c.name} style={{ paddingLeft: "1rem" }}>
                        <NavLink to={c.path} tag={NavLinkRRD} onClick={closeCollapse}>
                          <i className="ni ni-bold-right text-muted" />
                          {c.name}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Collapse>
                </React.Fragment>
              ) : (
                <NavItem key={item.name}>
                  <NavLink to={item.path} tag={NavLinkRRD} onClick={closeCollapse}>
                    <i className={item.icon} />
                    {item.name}
                  </NavLink>
                </NavItem>
              )
            )}
          </Nav>

          {/* Logout pinned to the bottom of the sidebar */}
          <Nav navbar className="mt-auto border-top pt-2">
            <NavItem>
              <NavLink onClick={handleLogout} style={{ cursor: "pointer" }}>
                <i className="ni ni-user-run text-red" />
                Logout
              </NavLink>
            </NavItem>
          </Nav>
        </Collapse>
      </Container>
    </Navbar>
  );
};

export default AdminSidebar;
